/**
 * authr.ts - Authr (Authentication) preferences module
 * 
 * CHANGE HISTORY:
 * - 251102_1750 [pending] danielbeer: Added client import, version awareness, and dev mode bypass
 * - 251102_1744 [pending] danielbeer: Added OAuth provider configuration with IdP help documentation
 * - 251031_1540 [pending] danielbeer: Added package metadata
 * - 251023_1854 [pending] danielbeer: Initial creation
 */

export const authrModule = {
  id: 'system-authr',
  name: 'Authentication',
  icon: '🔐',
  category: 'modules',
  order: 2,
  
  // Package metadata for info panel
  packageInfo: {
    name: '@opcentral/authr',
    version: '0.2.0+251021002750',
    displayVersion: '0.2.0',
    description: 'Authentication & authorization system for OpCentral',
    status: 'implemented',
    implementationProgress: 75,
    dependencies: {
      '@opcentral/core': '*',
      '@opcentral/actr': '*',
      'react': '^18.0.0'
    },
    keywords: ['authentication', 'authorization', 'oauth2', 'openid', 'biometric', 'webauthn', 'keycloak'],
    author: 'OpCentral',
    license: 'MIT'
  },
  
  settings: {
    sessionTimeout: {
      type: 'number',
      label: 'Session Timeout (minutes)',
      description: 'Automatically log out after this many minutes of inactivity',
      defaultValue: 60,
      min: 5,
      max: 1440
    },
    
    rememberMe: {
      type: 'boolean',
      label: 'Remember Me',
      description: 'Stay logged in across browser sessions',
      defaultValue: true
    },
    
    twoFactorAuth: {
      type: 'boolean',
      label: 'Two-Factor Authentication',
      description: 'Require additional verification when logging in',
      defaultValue: false
    },
    
    loginNotifications: {
      type: 'boolean',
      label: 'Login Notifications',
      description: 'Notify me of new login attempts',
      defaultValue: true
    },
    
    passwordExpiry: {
      type: 'select',
      label: 'Password Expiry',
      description: 'Require password change after this period',
      defaultValue: 'never',
      options: [
        { label: 'Never', value: 'never' },
        { label: '30 Days', value: '30' },
        { label: '60 Days', value: '60' },
        { label: '90 Days', value: '90' },
        { label: '180 Days', value: '180' }
      ]
    },
    
    // Development Mode Settings
    devModeBypass: {
      type: 'boolean',
      label: 'Development Mode: Bypass Authentication',
      description: '⚠️ WARNING: Disables authentication entirely. Only available in non-hosted environments.',
      defaultValue: false,
      warning: {
        title: 'Security Warning',
        message: 'Bypassing authentication is ONLY for local development. Never enable this in production or hosted environments. This setting requires admin privileges.',
        severity: 'critical'
      },
      visibilityCondition: {
        environment: 'development',
        hosted: false,
        requiresPermission: 'admin'
      },
      helpFile: 'oauth-providers.md',
      helpSection: 'devModeBypass'
    },
    
    // OAuth Provider Configuration
    oauthProviders: {
      type: 'array',
      label: 'OAuth Providers',
      description: 'Configure external authentication providers (Keycloak, Google, GitHub, etc.)',
      defaultValue: [],
      actions: {
        import: {
          label: 'Import Client Configuration',
          description: 'Import OAuth client settings from IdP export file',
          icon: '📥',
          handler: 'importOAuthClient',
          helpFile: 'oauth-providers.md',
          helpSection: 'importClient'
        }
      },
      itemSchema: {
        enabled: {
          type: 'boolean',
          label: 'Enabled',
          description: 'Enable this OAuth provider',
          defaultValue: true
        },
        provider: {
          type: 'select',
          label: 'Provider Type',
          description: 'Select the identity provider',
          defaultValue: 'keycloak',
          options: [
            { label: 'Keycloak', value: 'keycloak' },
            { label: 'Google', value: 'google' },
            { label: 'GitHub', value: 'github' },
            { label: 'Custom OAuth2', value: 'custom' }
          ],
          helpFile: 'oauth-providers.md'
        },
        name: {
          type: 'string',
          label: 'Provider Name',
          description: 'Display name for this provider (e.g., "Company SSO")',
          defaultValue: '',
          placeholder: 'My OAuth Provider'
        },
        clientId: {
          type: 'string',
          label: 'Client ID',
          description: 'OAuth client ID from your identity provider',
          defaultValue: '',
          placeholder: 'your-client-id',
          helpFile: 'oauth-providers.md',
          helpSection: 'clientId'
        },
        clientSecret: {
          type: 'password',
          label: 'Client Secret',
          description: 'OAuth client secret (keep this secure!)',
          defaultValue: '',
          placeholder: 'your-client-secret',
          helpFile: 'oauth-providers.md',
          helpSection: 'clientSecret'
        },
        authUrl: {
          type: 'string',
          label: 'Authorization URL',
          description: 'OAuth authorization endpoint',
          defaultValue: '',
          placeholder: 'https://auth.example.com/oauth/authorize',
          helpFile: 'oauth-providers.md',
          helpSection: 'authUrl'
        },
        tokenUrl: {
          type: 'string',
          label: 'Token URL',
          description: 'OAuth token endpoint',
          defaultValue: '',
          placeholder: 'https://auth.example.com/oauth/token',
          helpFile: 'oauth-providers.md',
          helpSection: 'tokenUrl'
        },
        scopes: {
          type: 'string',
          label: 'Scopes',
          description: 'Space-separated list of OAuth scopes',
          defaultValue: 'openid profile email',
          placeholder: 'openid profile email',
          helpFile: 'oauth-providers.md',
          helpSection: 'scopes'
        },
        redirectUri: {
          type: 'string',
          label: 'Redirect URI',
          description: 'OAuth callback URL (configure this in your IdP)',
          defaultValue: '',
          placeholder: 'http://localhost:3000/auth/callback',
          helpFile: 'oauth-providers.md',
          helpSection: 'redirectUri'
        }
      }
    }
  }
};

export default authrModule;
